import {
  PublicClientApplication,
  type Configuration,
  LogLevel,
} from '@azure/msal-browser';

// ── Environment Variables ──

const runtimeConfig = typeof window === 'undefined' ? undefined : window.__QCDOC_PORTAL_CONFIG__;
const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? runtimeConfig?.entraTenantId ?? '11111111-2222-3333-4444-555555555555';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? runtimeConfig?.entraClientId ?? 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee';
const redirectUri = import.meta.env.VITE_ENTRA_REDIRECT_URI ?? runtimeConfig?.entraRedirectUri ?? `${window.location.origin}/auth-callback.html`;
const postLogoutRedirectUri =
  import.meta.env.VITE_ENTRA_POST_LOGOUT_URI ?? runtimeConfig?.entraPostLogoutUri ?? window.location.origin;

/**
 * The API scope exposed by the back-end App Registration.
 * Typically: `api://<api-client-id>/access_as_user`
 */
const apiScope = import.meta.env.VITE_ENTRA_API_SCOPE ?? runtimeConfig?.entraApiScope ?? '';
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? runtimeConfig?.apiBaseUrl ?? '';

export const entraTenantId = tenantId;
export const entraRedirectUri = redirectUri;

export const isEntraConfigured = Boolean(
  tenantId !== '11111111-2222-3333-4444-555555555555' &&
  clientId !== 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee' &&
  apiScope && apiBaseUrl && URL.canParse(apiBaseUrl),
);

// MSAL needs the browser crypto API, which exists only on HTTPS and localhost; on plain HTTP it throws crypto_nonexistent.
const isSecureContext = typeof window === 'undefined' || window.isSecureContext !== false;

export const isMsalConfigured = Boolean(
  isSecureContext &&
  tenantId !== '11111111-2222-3333-4444-555555555555' &&
  clientId !== 'aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee',
);

// ── MSAL Configuration ──

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error('[MSAL]', message);
            break;
          case LogLevel.Warning:
            console.warn('[MSAL]', message);
            break;
          case LogLevel.Info:
            console.info('[MSAL]', message);
            break;
          case LogLevel.Verbose:
            console.debug('[MSAL]', message);
            break;
        }
      },
    },
  },
};

// ── MSAL Instance (lazy singleton) ──

// Constructed on demand: MSAL throws when clientId/authority are empty, which
// would break the app in local or dev auth mode where Entra is not configured.
let _msalInstance: PublicClientApplication | null = null;

export function getMsalInstance(): PublicClientApplication {
  if (!isMsalConfigured) {
    throw new Error(
      'Entra ID requires a registered tenant, client, API scope, and API origin. ' +
        'The MockupCorp identifiers are placeholders and cannot authenticate users.',
    );
  }
  _msalInstance ??= new PublicClientApplication(msalConfig);
  return _msalInstance;
}

// ── Scopes ──

/** Used for loginRedirect — requests the API scope + openid/profile */
export const loginRequest = {
  prompt: 'select_account' as const,
  redirectUri,
  scopes: apiScope ? [apiScope, 'openid', 'profile', 'email'] : ['openid', 'profile', 'email'],
};

/** Used for acquireTokenSilent when calling the backend API */
export const tokenRequest = {
  scopes: apiScope ? [apiScope] : [],
};
